import { DossbotEventType } from '@doss/prisma/types'
import { z } from 'zod'

export const chatTitleUpdatedDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.CHAT_TITLE_UPDATED),
    title: z.string(),
  })
  .strict()

export type ChatTitleUpdatedDossbotEventData = z.infer<
  typeof chatTitleUpdatedDossbotEventDataSchema
>

export const chatSummaryUpdatedDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.CHAT_SUMMARY_UPDATED),
    summary: z.string(),
  })
  .strict()

export type ChatSummaryUpdatedDossbotEventData = z.infer<
  typeof chatSummaryUpdatedDossbotEventDataSchema
>

export const chatRunStartedDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.CHAT_RUN_STARTED),
  })
  .strict()

export type ChatRunStartedEventData = z.infer<typeof chatRunStartedDossbotEventDataSchema>

export const chatRunCompletedDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.CHAT_RUN_COMPLETED),
  })
  .strict()

export type ChatRunCompletedEventData = z.infer<typeof chatRunCompletedDossbotEventDataSchema>

export const chatRunFailedDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.CHAT_RUN_FAILED),
    error: z.string(),
  })
  .strict()

export type ChatRunFailedEventData = z.infer<typeof chatRunFailedDossbotEventDataSchema>

export const chatRunCanceledDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.CHAT_RUN_CANCELED),
  })
  .strict()

export type ChatRunCanceledEventData = z.infer<typeof chatRunCanceledDossbotEventDataSchema>

export const agentRunStartedDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.AGENT_RUN_STARTED),
  })
  .strict()

export type AgentRunStartedEventData = z.infer<typeof agentRunStartedDossbotEventDataSchema>

export const agentRunCompletedDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.AGENT_RUN_COMPLETED),
    text: z.string(),
  })
  .strict()

export type AgentRunCompletedEventData = z.infer<typeof agentRunCompletedDossbotEventDataSchema>

export const agentRunFailedDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.AGENT_RUN_FAILED),
    error: z.string(),
  })
  .strict()

export type AgentRunFailedEventData = z.infer<typeof agentRunFailedDossbotEventDataSchema>

export const agentRunCanceledDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.AGENT_RUN_CANCELED),
  })
  .strict()

export type AgentRunCanceledEventData = z.infer<typeof agentRunCanceledDossbotEventDataSchema>

export const toolRunPendingDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.TOOL_RUN_PENDING),
    awaitingToolConfirmationMessage: z.string().optional(),
  })
  .strict()

export type ToolRunPendingEventData = z.infer<typeof toolRunPendingDossbotEventDataSchema>

export const toolRunStartedDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.TOOL_RUN_STARTED),
    runningToolMessage: z.string(),
  })
  .strict()

export type ToolRunStartedEventData = z.infer<typeof toolRunStartedDossbotEventDataSchema>

export const toolRunApprovedDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.TOOL_RUN_APPROVED),
    approvedToolMessage: z.string(),
  })
  .strict()

export type ToolRunApprovedEventData = z.infer<typeof toolRunApprovedDossbotEventDataSchema>

export const toolRunRejectedDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.TOOL_RUN_REJECTED),
    rejectedToolMessage: z.string(),
  })
  .strict()

export type ToolRunRejectedEventData = z.infer<typeof toolRunRejectedDossbotEventDataSchema>

export const toolRunCompletedDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.TOOL_RUN_COMPLETED),
    completedToolMessage: z.string(),
  })
  .strict()

export type ToolRunCompletedEventData = z.infer<typeof toolRunCompletedDossbotEventDataSchema>

export const toolRunFailedDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.TOOL_RUN_FAILED),
    failedToolMessage: z.string(),
    error: z.string(),
  })
  .strict()

export type ToolRunFailedEventData = z.infer<typeof toolRunFailedDossbotEventDataSchema>

export const toolRunCanceledDossbotEventDataSchema = z
  .object({
    type: z.literal(DossbotEventType.TOOL_RUN_CANCELED),
    canceledToolMessage: z.string(),
  })
  .strict()

export type ToolRunCanceledEventData = z.infer<typeof toolRunCanceledDossbotEventDataSchema>

/** Discriminated union of all Dossbot event data types */
export const dossbotEventDataSchema = z.discriminatedUnion('type', [
  chatTitleUpdatedDossbotEventDataSchema,
  chatSummaryUpdatedDossbotEventDataSchema,
  chatRunStartedDossbotEventDataSchema,
  chatRunCompletedDossbotEventDataSchema,
  chatRunFailedDossbotEventDataSchema,
  chatRunCanceledDossbotEventDataSchema,
  agentRunStartedDossbotEventDataSchema,
  agentRunCompletedDossbotEventDataSchema,
  agentRunFailedDossbotEventDataSchema,
  agentRunCanceledDossbotEventDataSchema,
  toolRunApprovedDossbotEventDataSchema,
  toolRunRejectedDossbotEventDataSchema,
  toolRunPendingDossbotEventDataSchema,
  toolRunStartedDossbotEventDataSchema,
  toolRunCompletedDossbotEventDataSchema,
  toolRunFailedDossbotEventDataSchema,
  toolRunCanceledDossbotEventDataSchema,
])

/** Discriminated union of all Dossbot event data types */
export type DossbotEventData = z.infer<typeof dossbotEventDataSchema>
